class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    // search stuff
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");

    // ✅ IMPORTANT: se questo è null, l'anteprima non può funzionare
    this.predictiveSearchResults =
      this.querySelector("[data-predictive-search]") ||
      this.querySelector(".predictive-search") ||
      this.querySelector(".wt-predictive-search");

    // ✅ fallback: se manca data-search-properties usiamo un default sensato
    this.searchProperties =
      this.dataset.searchProperties ||
      "title,product_type,variants.title,vendor";

    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.emptyAnnouncement = this.querySelector(".search-empty");
    this.searchResultTop = this.querySelector(".search-result-top");

    this.isVisibleClearButton = false;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);
    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];

    this.suggestionMenuContent = this.querySelector(".search-result-top")
      ? this.querySelector(".search-result-top").cloneNode(true)
      : document.createElement("div");

    this.timeOut = null;
    this.init();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    const list = focusableElements();
    return {
      focusableElements,
      first: list[0],
      last: list[list.length - 1],
    };
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(this.toggleTabindexElements, "-1");
      setTabindex([this.mainTrigger], "0");
      this.isOpen = false;

      setTimeout(() => {
        this.mainTrigger?.focus();
      }, 0);
    } else {
      this.setAttribute("open", "");
      setTabindex(this.toggleTabindexElements, "0");
      setTabindex([this.mainTrigger], "-1");

      this.input?.focus();
      this.input?.setSelectionRange(
        this.input.value.length,
        this.input.value.length,
      );

      // 🔧 FIX: mostra il clear button se c'è già testo
      if (this.input && this.input.value.length > 0) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;

        // ✅ opzionale: se riapri con testo già scritto, rigenera risultati
        this.onChange();
      }

      this.isOpen = true;
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer?.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    const isPredictiveSearchAvailable = this.hidePredictiveSearch();
    if (!isPredictiveSearchAvailable) return;

    // ✅ Se manca il contenitore risultati, non ha senso proseguire
    if (!this.predictiveSearchResults) return;

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.clearButton?.addEventListener("click", () => {
      if (this.input) this.input.value = "";
      this.clearButton.style.display = "none";
      this.clearButton.setAttribute("tabindex", "-1");
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) this.toggleDrawerClasses();
      }

      if (isTabPressed && this.isOpen && first && last) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.input?.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });
  }

  hidePredictiveSearch() {
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      const featuresData = JSON.parse(featuresTag.textContent);
      if (featuresData.predictiveSearch === false) {
        const searchPageUrl = this.dataset.searchPageUrl;
        this.triggers().forEach((trigger) => {
          trigger.addEventListener("click", (e) => {
            e.preventDefault();
            window.location.href = searchPageUrl;
          });
        });
        return false;
      }
    }
    return true;
  }

  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form?.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input?.addEventListener(
      "input",
      debounce(() => {
        this.onChange();
      }, 300),
    );

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer?.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return (this.input?.value || "").trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!this.predictiveSearchResults) return;

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replaceAll(" ", "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product,page,article,collection,query` +
      `&resources[limit_scope]=each` +
      `&resources[limit]=6` +
      `&resources[options][fields]=${encodeURIComponent(this.searchProperties)}` +
      `&section_id=predictive-search`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");

        // ✅ più robusto: se non trova l'id, prova fallback
        const section =
          doc.querySelector("#shopify-section-predictive-search") ||
          doc.querySelector('[id*="predictive-search"]') ||
          doc.body;

        const resultsMarkup = section ? section.innerHTML : "";

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch(() => {
        // se la fetch fallisce, almeno non “muore” tutto
        this.clearResults();
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
  }

  clearResults() {
    if (!this.predictiveSearchResults) return;

    this.removeAttribute("results");
    this.setAttribute("empty", true);

    // ✅ non svuotiamo l'input qui: l’input lo gestisce il tasto X
    this.predictiveSearchResults.innerHTML =
      this.suggestionMenuContent.innerHTML || "";
  }
}

customElements.define("search-drawer", DrawerSearchSection);
